import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { ExternalLink as IconExternalLink } from 'react-feather';
import { Link } from 'react-router-dom';

// import { CodeIframe } from './CodeIframe';
import { CodeDemoPreview } from './CodeDemoPreview';

import S from './CodeDemo.module.css';
import { Code } from '../../pkg/Code';
import { FacebookShareButton } from '../../pkg/Social/Facebook/Share';
import { TwitterShareButton } from '../../pkg/Social/Twitter/Share';
import Tabs from '../../pkg/Tabs';

import useContainerWidth from '../../hooks/useContainerWidth';

import { type DemoType } from './types';

type DisplayType = 'html' | 'css' | 'js' | 'preview';

interface Props {
  data: DemoType;
  url?: string;
  display?: DisplayType[];
  layout?:
    | 'classic'
    | 'columns'
    | 'bottom-results'
    | 'right-results'
    | 'tabs-columns'
    | 'tabs-rows';
  height?: number;
  isEmbed?: boolean;
  style?: { [key: string]: number | string };

  allowLayoutChange?: boolean;

  isNarrow?: boolean;
}

const defaultDisplay: DisplayType[] = ['html', 'css', 'js', 'preview'];
const layoutTabs = ['classic', 'tabs'];

const RightResultsLayout = ({ data, display = defaultDisplay }: Props) => {
  const codeBlocksCount = [data.html, data.css, data.js].filter(Boolean).length;
  return (
    <>
      {display.includes('html') && data.html && (
        <div
          className={S.code}
          style={{ height: `${(1 / codeBlocksCount) * 100}%` }}
        >
          <div className={S.title}>HTML</div>
          <Code preClassName={S.pre} language="html">
            {data.html}
          </Code>
        </div>
      )}
      {display.includes('css') && data.css && (
        <div
          className={S.code}
          style={{ height: `${(1 / codeBlocksCount) * 100}%` }}
        >
          <div className={S.title}>CSS</div>
          <Code preClassName={S.pre} language="css">
            {data.css}
          </Code>
        </div>
      )}
      {display.includes('js') && data.js && (
        <div
          className={S.code}
          style={{ height: `${(1 / codeBlocksCount) * 100}%` }}
        >
          <div className={S.title}>JavaScript</div>
          <Code preClassName={S.pre} language="js">
            {data.js}
          </Code>
        </div>
      )}
    </>
  );
};

const tabToLangMap = {
  HTML: 'html',
  CSS: 'css',
  JavaScript: 'js'
};

const TabsColumnsLayout = ({
  display = defaultDisplay,
  data,
  isNarrow
}: Props) => {
  const [currentTab, setCurrentTab] = useState('');

  const tabs = useMemo(
    () =>
      [
        display.includes('html') && data.html ? 'HTML' : '',
        display.includes('css') && data.css ? 'CSS' : '',
        display.includes('js') && data.js ? 'JavaScript' : '',
        isNarrow ? 'Preview' : ''
      ].filter(Boolean),
    [display, data, isNarrow]
  );

  const handleSelect = useCallback((e, tab) => {
    setCurrentTab(tab);
  }, []);

  const selectedTab =
    currentTab || (isNarrow ? tabs[tabs.length - 1] : tabs[0]);

  const tab = !isNarrow && selectedTab === 'Preview' ? tabs[0] : selectedTab;

  return (
    <>
      {tabs.length > 1 && (
        <Tabs
          tabs={tabs}
          selected={tab}
          className={S.tabs}
          onSelect={handleSelect}
        />
      )}
      {tabToLangMap[tab] && (
        <Code preClassName={S.pre} language={tabToLangMap[tab]}>
          {data[tabToLangMap[tab]]}
        </Code>
      )}
      {tab === 'Preview' && isNarrow && (
        <CodeDemoPreview
          className={S.previewIframe}
          html={data.html}
          css={data.css}
          js={data.js}
        />
      )}
    </>
  );
};

const layoutMap = {
  classic: 'right-results',
  tabs: 'tabs-columns'
};
const flip = (data) =>
  Object.fromEntries(Object.entries(data).map(([key, value]) => [value, key]));

const layoutShortMap = flip(layoutMap);

const layoutComponent = {
  'right-results': RightResultsLayout,
  'tabs-columns': TabsColumnsLayout
};

const ShareButtons = ({ title, url }) => (
  <>
    <FacebookShareButton className={S.shareButton} />
    <TwitterShareButton
      className={S.shareButton}
      text={title}
      via="Vasyl_Zubach"
    />

    {url && <span className={S.actionDivider} />}
    {url && (
      <Link to={url} className={S.shareButton}>
        <IconExternalLink width={16} height={16} />
      </Link>
    )}
  </>
);

export const CodeDemo = (props: Props) => {
  const { url, data, display = defaultDisplay, height } = props;

  const [layout, setLayout] = useState(props.layout || 'tabs-columns');

  const rootRef = useRef<HTMLDivElement>(null);
  const rootWidth = useContainerWidth(rootRef);
  const isNarrow = rootWidth ? rootWidth < 600 : true;

  // console.log({ rootWidth, isNarrow });
  const handleLayoutChange = useCallback(
    (event: SyntheticEvent<HTMLElement>, tab) => {
      setLayout(layoutMap[typeof tab === 'string' ? tab : tab.title]);
    },
    [setLayout]
  );

  const shouldDisplayCode =
    (display.includes('html') && data.html) ||
    (display.includes('css') && data.css) ||
    (display.includes('js') && data.js);

  const style = useMemo(
    () => ({ ...props.style, height }),
    [height, props.style]
  );

  const shouldShowLayoutTabs =
    shouldDisplayCode && props.allowLayoutChange !== false;

  const ResultsLayout = layoutComponent[layout];

  return (
    <div
      className={S.root}
      // data-layout={layout}
      style={style}
      data-embed={props.isEmbed || false}
      // data-narrow={String(isNarrow)}
      ref={rootRef}
    >
      <div className={S.header}>
        <div className={S.headerTitle}>{data.title}</div>

        <div className={S.actions}>
          {!isNarrow && shouldShowLayoutTabs && (
            <>
              <Tabs
                tabs={layoutTabs}
                selected={layoutShortMap[layout]}
                onSelect={handleLayoutChange}
              />
              <span className={S.actionDivider} />
            </>
          )}

          <ShareButtons url={url} title={data.title} />
        </div>
      </div>

      <div className={S.content} data-narrow={String(isNarrow)}>
        {isNarrow && (
          <TabsColumnsLayout {...props} display={display} isNarrow />
        )}

        {!isNarrow && (
          <>
            {shouldDisplayCode && (
              <div className={S.codeContainer}>
                <ResultsLayout
                  {...props}
                  display={display}
                  layout={layout}
                  isNarrow={isNarrow}
                />
              </div>
            )}
            <div className={S.preview}>
              <CodeDemoPreview
                className={S.previewIframe}
                html={data.html}
                css={data.css}
                js={data.js}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
